import { Box, Flex, Img, keyframes, Stack, Text } from '@chakra-ui/react'
import { Body1, H1 } from 'components/typography'
import { FC } from 'react'

import { HOME_HERO_ID } from '../constants'
import { HOME_BENEFITS, HOME_HERO, HOME_HERO_AVAILABILITY_BADGE } from '../content'
import HomeHeroCta from './HomeHeroCta'

const onlinePulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.55); }
  70% { box-shadow: 0 0 0 8px rgba(34, 197, 94, 0); }
`

/** Chakra `lg` breakpoint (62em), used by the `<picture>` sources below. */
const DESKTOP_MEDIA = '(min-width: 62em)'

/** Resized copies of the hero photo in `/public/landings/medleve` (`hero-doctor-<width>.webp`). */
const HERO_IMAGE_BASE = '/landings/medleve/hero-doctor'
const HERO_IMAGE_WIDTHS = [828, 1600, 2400]

interface Seal {
  alt: string
  /** Light artwork used over the dark mobile gradient. */
  mobileSrc: string
  desktopSrc: string
  width: number
  height: number
}

/**
 * Same seals as the current landing (mobile uses the light Anvisa/LGPD artwork).
 * REQUIRES LEGAL REVIEW — the Anvisa / SSL / LGPD seal artwork implies regulatory and compliance claims.
 */
const SEALS: Seal[] = [
  {
    alt: 'Anvisa',
    mobileSrc: '/landings/Anvisa-light.png',
    desktopSrc: '/landings/Anvisa.png',
    width: 121,
    height: 183
  },
  { alt: 'SSL', mobileSrc: '/landings/SSL.png', desktopSrc: '/landings/SSL.png', width: 127, height: 183 },
  { alt: 'LGPD', mobileSrc: '/landings/LGPD-light.png', desktopSrc: '/landings/LGPD.png', width: 124, height: 183 }
]

/**
 * Hero + benefits of the organic homepage.
 *
 * Visually it is the hero of the current Medleve landing (`MedleveHeroSection` / `MedleveHeroMobile` +
 * `MedleveBanner`), but written as ONE responsive DOM instead of two JS-switched trees, so the server
 * HTML has a single `<h1>` and no duplicated copy (PRD §11, §38, §76):
 * - mobile: full-height photo with gradient, copy + CTA, seals at the bottom, benefits as tiles below;
 * - desktop (`lg`): full-viewport photo, copy + CTA bottom-left, seals top-right, benefits bottom-right
 *   (the benefits list shares the hero grid cell through CSS grid, it is not duplicated).
 */
const HomeHero: FC = () => (
  <Box
    as="section"
    aria-labelledby="home-hero-title"
    display="grid"
    gridTemplateColumns="minmax(0, 1fr)"
    bg="alabaster"
  >
    <Flex
      id={HOME_HERO_ID}
      gridArea={{ lg: '1 / 1' }}
      direction="column"
      justify={{ lg: 'flex-end' }}
      align={{ lg: 'center' }}
      position="relative"
      w="full"
      // min-height only: the block grows with its copy instead of clipping it on short viewports.
      minH={{ base: 'calc(100vh - 64px)', md: '720px', lg: 'max(810px, 100vh)' }}
      overflow="hidden"
      sx={{ isolation: 'isolate' }}
    >
      <Img
        // Same photo as the current landing (`/landings/doctor-using-smarphone.webp`, 7360x4912), served as
        // resized copies: decoding the 36-megapixel original was most of the LCP render delay on phones.
        src={`${HERO_IMAGE_BASE}-1600.webp`}
        srcSet={HERO_IMAGE_WIDTHS.map((width) => `${HERO_IMAGE_BASE}-${width}.webp ${width}w`).join(', ')}
        // `object-fit: cover` in a portrait box is height-bound: the photo (3:2) renders about 150vh wide.
        sizes={`${DESKTOP_MEDIA} 100vw, 150vh`}
        alt={HOME_HERO.imageAlt}
        htmlWidth={1600}
        htmlHeight={1068}
        // LCP image: never lazy, fetched first. (Lower-case attribute: React 18.2 has no `fetchPriority` prop.)
        loading="eager"
        {...{ fetchpriority: 'high' }}
        position="absolute"
        top={0}
        left={0}
        w="full"
        h="full"
        objectFit="cover"
        objectPosition={{ base: '50% 25%', lg: '40%' }}
        zIndex={-2}
      />
      <Box
        aria-hidden="true"
        position="absolute"
        top={0}
        left={0}
        w="full"
        h="full"
        zIndex={-1}
        bg={{
          base: 'linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.65) 50%, rgba(0,0,0,0.92) 100%)',
          lg: 'rgba(0, 0, 0, 0.4)'
        }}
      />

      <Flex
        direction="column"
        flex={{ base: 1, lg: 'none' }}
        w="full"
        maxW={{ lg: '1600px' }}
        px={{ base: '20px', lg: '48px' }}
        pt={{ base: '96px', lg: '120px' }}
        pb={{ base: '28px', lg: 12 }}
        color="alabaster"
      >
        <Stack
          spacing={{ base: 6, lg: 10 }}
          my={{ base: 'auto', lg: 0 }}
          py={{ base: 8, lg: 0 }}
          // Leaves room for the benefits column that shares the grid cell on desktop.
          maxW={{ lg: 'min(650px, calc(100% - 440px))' }}
        >
          <Flex
            display={{ base: 'flex', lg: 'none' }}
            align="center"
            gap={2}
            alignSelf="flex-start"
            px={3}
            py="6px"
            borderRadius="full"
            bg="rgba(0,0,0,0.55)"
            border="1px solid rgba(255,255,255,0.18)"
            backdropFilter="blur(8px)"
          >
            <Box
              aria-hidden="true"
              w="8px"
              h="8px"
              borderRadius="full"
              bg="#22C55E"
              animation={`${onlinePulse} 1.8s ease-in-out infinite`}
            />
            <Text fontSize="13px" fontWeight={600} letterSpacing="-0.01em">
              {HOME_HERO_AVAILABILITY_BADGE}
            </Text>
          </Flex>

          {/* REQUIRES MEDICAL REVIEW — PRD §11–13 hero copy (see content.ts). */}
          <H1
            id="home-hero-title"
            aria-describedby="home-hero-note"
            fontSize={{ base: '30px', sm: '34px', lg: '3.25rem' }}
            letterSpacing={{ base: '-0.02em', lg: '-0.03em' }}
            lineHeight={{ base: 1.1, lg: '60px' }}
          >
            {HOME_HERO.h1}
          </H1>

          <Stack spacing={{ base: 3, lg: 4 }}>
            <Body1
              fontWeight={{ base: 400, lg: 800 }}
              fontSize={{ base: '17px', lg: '1.5rem' }}
              letterSpacing={{ base: '-0.01em', lg: '-0.03em' }}
              lineHeight={{ base: 1.5, lg: '1.75rem' }}
            >
              {HOME_HERO.subheadline}
            </Body1>
            <Body1
              // Lighter accent than the landing's `#99b9e9` on desktop too: that tone was hard to read over the photo.
              color="#b8d4ff"
              fontWeight={700}
              fontSize={{ base: '15px', lg: '17px' }}
              lineHeight={{ base: 1.4, lg: '24px' }}
            >
              {HOME_HERO.complementary}
            </Body1>
          </Stack>

          <HomeHeroCta />

          {/* PRD v4 §15: note the H1 asterisk refers to. */}
          <Body1 id="home-hero-note" fontSize={{ base: '13px', lg: '14px' }} lineHeight={1.4} opacity={0.9}>
            {HOME_HERO.note}
          </Body1>
        </Stack>

        <Flex
          as="ul"
          aria-label="Selos de segurança e conformidade"
          listStyleType="none"
          m={0}
          p={0}
          justify={{ base: 'space-around', lg: 'flex-start' }}
          align="center"
          position={{ lg: 'absolute' }}
          top={{ lg: '100px' }}
          right={{ lg: '48px' }}
        >
          {SEALS.map(({ alt, mobileSrc, desktopSrc, width, height }) => (
            <Box as="li" key={alt}>
              <picture>
                <source media={DESKTOP_MEDIA} srcSet={desktopSrc} />
                <Img
                  src={mobileSrc}
                  alt={alt}
                  htmlWidth={width}
                  htmlHeight={height}
                  h={{ base: '88px', lg: '100px' }}
                  w="auto"
                  objectFit="contain"
                />
              </picture>
            </Box>
          ))}
        </Flex>
      </Flex>
    </Flex>

    {/* REQUIRES MEDICAL REVIEW — benefits (see content.ts). */}
    <Box
      as="ul"
      aria-label="Benefícios"
      listStyleType="none"
      m={0}
      gridArea={{ lg: '1 / 1' }}
      alignSelf={{ lg: 'end' }}
      justifySelf={{ lg: 'end' }}
      zIndex={{ lg: 1 }}
      display="grid"
      gridTemplateColumns={{ base: 'repeat(2, minmax(0, 160px))', lg: 'minmax(0, 1fr)' }}
      justifyContent={{ base: 'center', lg: 'stretch' }}
      gap={4}
      w={{ lg: 'max-content' }}
      maxW={{ lg: '400px' }}
      px={{ base: 6, lg: 0 }}
      pt={{ base: 6, lg: 0 }}
      mb={{ lg: 12 }}
      mr={{ lg: 'max(48px, calc((100% - 1600px) / 2 + 48px))' }}
      color={{ base: 'medleve', lg: 'alabaster' }}
    >
      {HOME_BENEFITS.map((benefit) => (
        <Flex
          as="li"
          key={benefit}
          align="center"
          justify={{ base: 'center', lg: 'flex-start' }}
          minH={{ base: '90px', lg: 'auto' }}
          px={{ base: 2, lg: 4 }}
          py={4}
          bg={{ base: 'white', lg: 'rgba(31, 30, 30, 0.4)' }}
          backdropFilter={{ lg: 'blur(20px)' }}
          borderRadius={{ base: 'lg', lg: '24px' }}
        >
          <Text
            textAlign={{ base: 'center', lg: 'left' }}
            fontWeight={{ base: 800, lg: 400 }}
            fontSize={{ base: '1rem', lg: '1.5rem' }}
            sx={{ fontFeatureSettings: "'ss01' on, 'liga' off, 'calt' off" }}
            letterSpacing={{ base: '-0.01em', lg: '-0.03em' }}
            lineHeight={{ base: '1.25rem', lg: '1.75rem' }}
          >
            {benefit}
          </Text>
        </Flex>
      ))}
    </Box>
  </Box>
)

export default HomeHero
